import { PAGE_SIZE } from '../../constants'
import { fetchWithToken } from '../fetcher'

const ADD_TO_WISHLIST = `
mutation getAddProductsToWishlist(
    $wishlistId: ID!
    $wishlistItems: [WishlistItemInput!]!
  ) {
    addProductsToWishlist(
      wishlistId: $wishlistId
      wishlistItems: $wishlistItems
    ) {
      user_errors {
        code
        message
      }
      wishlist {
        id
        items_count
        updated_at
        sharing_code
      }
    }
  }
`
const GET_WISHLIST = `
query getWishlists($currentPage: Int, $pageSize: Int) {
    customer {
      wishlists(currentPage: $currentPage, pageSize: $pageSize) {
        id
        items_count
        updated_at
        sharing_code
        items_v2 {
          items {
            ...on ConfigurableWishlistItem{
              added_at
              quantity
              configured_variant{
                __typename
                uid
                
                sku
                name
                url_key
                image{
                  url
                  label
                }
                thumbnail{
                  url
                  label
                }
              }
              configurable_options {
                configurable_product_option_uid
                configurable_product_option_value_uid
                option_label
                value_label
              }
            }
            added_at
            quantity
            id
            description
           
            product {
              __typename
              name
              image {
                ...ProductImageFields
              }
              thumbnail {
                ...ProductImageFields
              }
              sku
              uid
              url_key
              url_path
              stock_status
              daily_sale {
                end_date
                entity_id
                product_id
                sale_price
                sale_qty
                saleable_qty
              }
              special_price
              special_to_date
              new_from_date
              new_to_date
              price_range {
                ...PriceRangeField
              }
            }
            
          }
          page_info {
            total_pages
            page_size
            current_page
          }
        }
      }
    }
  }
  fragment PriceRangeField on PriceRange {
    __typename
    maximum_price {
      ...ProductPriceField
    }
    minimum_price {
      ...ProductPriceField
    }
  }
  fragment ProductPriceField on ProductPrice {
    discount {
      amount_off
      percent_off
    }
    final_price {
      ...MoneyFields
    }
    regular_price {
      ...MoneyFields
    }
  }
  fragment ProductImageFields on ProductImage {
    url
  }
  fragment MoneyFields on Money {
    currency
    value
  }`

const REMOVE_ITEM_FROM_WISHLIST = `
mutation removeProductsFromWishlist(
  $wishlistId: ID!
  $wishlistItemsIds: [ID!]!
) {
  removeProductsFromWishlist(
    wishlistId: $wishlistId
    wishlistItemsIds: $wishlistItemsIds
  ) {
    user_errors {
      code
      message
    }
    wishlist {
      id
      items_count
      sharing_code
      updated_at
    }
  }
}
`

const ADD_WISHLIST_TO_CART = `
mutation addWishlistItemsToCart($wishlistId: ID!, $wishlistItemIds: [ID!]) {
  addWishlistItemsToCart(
    wishlistId: $wishlistId
    wishlistItemIds: $wishlistItemIds
  ) {
    add_wishlist_items_to_cart_user_errors {
      code
      message
      wishlistId
      wishlistItemId
    }
    status
    wishlist {
      id
      items_count
      sharing_code
      updated_at
    }
  }
}`

const addToWishlist = async ({ wishlistId, wishlistItems }: IWishlistFetching<IWishlistItemsDefault>) => {
  try {
    const response = await fetchWithToken({
      query: ADD_TO_WISHLIST,
      variables: {
        wishlistId: wishlistId,
        wishlistItems: [wishlistItems],
      },
    }).then((res) => res?.payload)
    if (
      response?.data?.addProductsToWishlist?.user_errors?.length > 0 ||
      !response ||
      response?.errors?.length > 0
    ) {
      return {
        data: null,
        message:
          response?.errors?.[0]?.message ?? response?.data?.addProductsToWishlist?.user_errors?.[0]?.message,
        status: 'error',
      }
    }
    return { status: 'success', message: 'Add product to wishlist successfully' }
  } catch (err) {
    console.log(err)
    return null
  }
}

const getWishlist = async ({ page = 1, pageSize = PAGE_SIZE }: { page?: number; pageSize?: number }) => {
  try {
    const response = await fetchWithToken(
      {
        query: GET_WISHLIST,
        variables: {
          currentPage: page,
          pageSize: pageSize,
        },
      },
      {
        next: { revalidate: 300, tags: ['wishlist-list'] },
      }
    ).then((res) => res?.payload)
    const wishlist = response?.data?.customer?.wishlists?.[0]
    if (!wishlist) return { data: null, message: response?.errors?.[0]?.message, status: 'error' }
    return wishlist
  } catch (err) {
    console.log(err)
    return { data: null, message: JSON.stringify(err), status: 'error' }
  }
}

const removeItemFromWishlist = async (data: IWishlistFetching<string>) => {
  try {
    const response = await fetchWithToken({
      query: REMOVE_ITEM_FROM_WISHLIST,
      variables: {
        wishlistId: data?.wishlistId,
        wishlistItemsIds: data?.wishlistItems,
      },
    }).then((res) => res?.payload)
    const wishlist = response?.data?.removeProductsFromWishlist
    if (!wishlist || wishlist?.user_errors?.length > 0)
      return {
        data: null,
        message: response?.errors?.[0]?.message ?? wishlist?.user_errors?.[0]?.message,
        status: 'error',
      }
    return { data: wishlist, message: 'Remove item from wishlist successfully', status: 'success' }
  } catch (err) {
    console.log(err)
    return { data: null, message: JSON.stringify(err), status: 'error' }
  }
}

const addWishlistToCart = async (data: IWishlistFetching<string>) => {
  try {
    const response = await fetchWithToken({
      query: ADD_WISHLIST_TO_CART,
      variables: {
        wishlistId: data?.wishlistId,
        wishlistItemIds: data?.wishlistItems,
      },
    }).then((res) => res?.payload)
    const wishlist = response?.data?.addWishlistItemsToCart
    if (!wishlist || wishlist?.add_wishlist_items_to_cart_user_errors?.length > 0)
      return {
        data: null,
        message:
          response?.errors?.[0]?.message ?? wishlist?.add_wishlist_items_to_cart_user_errors?.[0]?.message,
        status: 'error',
      }
    return { status: 'success', message: 'Add item from wishlist to cart successfully', data: wishlist }
  } catch (err) {
    console.log(err)
    return { data: null, message: JSON.stringify(err), status: 'error' }
  }
}

export { ADD_TO_WISHLIST, addToWishlist, addWishlistToCart, getWishlist, removeItemFromWishlist }
